import {
    createContext,
    useEffect,
    ReactNode,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';

type Measurements = Record<string, number>;

interface ReportMeasurementContextType {
    measurements: Measurements;
    registerHeight: (id: string, height: number) => void;
    getHeight: (id: string) => number;
    hasMeasurement: (id: string) => boolean;
    areMeasurementsReady: (ids: readonly string[]) => boolean;
}

const ReportMeasurementContext = createContext<ReportMeasurementContextType | undefined>(undefined);

export function useReportMeasurement() {
    const context = useContext(ReportMeasurementContext);
    if (!context) {
        throw new Error('useReportMeasurement must be used within a ReportMeasurementProvider');
    }
    return context;
}

interface ReportMeasurementProviderProps {
    children: ReactNode;
}

export function ReportMeasurementProvider({ children }: ReportMeasurementProviderProps) {
    const [measurements, setMeasurements] = useState<Measurements>({});
    const measurementsRef = useRef<Measurements>({});
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const registerHeight = useCallback((id: string, height: number) => {
        const hasExistingMeasurement = Object.prototype.hasOwnProperty.call(
            measurementsRef.current,
            id,
        );

        if (
            hasExistingMeasurement &&
            Math.abs((measurementsRef.current[id] || 0) - height) < 0.25
        ) {
            return;
        }

        measurementsRef.current[id] = height;

        // Debounce the state update to avoid thrashing
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            setMeasurements({ ...measurementsRef.current });
        }, 100);
    }, []);

    const getHeight = useCallback((id: string) => {
        return measurementsRef.current[id] || 0;
    }, []);

    const hasMeasurement = useCallback((id: string) => {
        return Object.prototype.hasOwnProperty.call(measurementsRef.current, id);
    }, []);

    const areMeasurementsReady = useCallback(
        (ids: readonly string[]) => ids.every((id) => hasMeasurement(id)),
        [hasMeasurement],
    );

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <ReportMeasurementContext.Provider
            value={{
                measurements,
                registerHeight,
                getHeight,
                hasMeasurement,
                areMeasurementsReady,
            }}
        >
            {children}
        </ReportMeasurementContext.Provider>
    );
}

/**
 * Provides a measurement store only if one is not already present in the tree.
 * When an ancestor already renders a `ReportMeasurementProvider` (e.g. the report
 * editor keeps one mounted above the cover-view toggle so pagination measurements
 * survive the round-trip), children reuse it instead of getting a fresh, empty store.
 */
export function OptionalReportMeasurementProvider({ children }: ReportMeasurementProviderProps) {
    const existing = useContext(ReportMeasurementContext);
    if (existing) return <>{children}</>;
    return <ReportMeasurementProvider>{children}</ReportMeasurementProvider>;
}
